import React, { useState, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Eye, EyeOff, CheckCircle2, XCircle, Globe } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import Brand from "@/components/Brand";

const SYMBOLS_REGEX = /[!@#$%^&*()_+\-=[\]{};:,.<>?/|~]/;

function pwChecks(pw) {
  return {
    length: pw.length >= 8,
    letter: /[A-Za-z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: SYMBOLS_REGEX.test(pw),
  };
}

function Check({ ok, label }) {
  return (
    <div className={`flex items-center gap-1 text-[11px] ${ok ? "text-emerald-700" : "text-gray-400"}`}>
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {label}
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang, setLang } = useT();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", new_password_confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const checks = useMemo(() => pwChecks(form.new_password), [form.new_password]);
  const pwOk = Object.values(checks).every(Boolean);
  const pwMatches = form.new_password && form.new_password === form.new_password_confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: form.new_password,
        new_password_confirm: form.new_password_confirm,
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("save_failed"));
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pattern px-4">
        <div className="bg-white rounded-2xl card-shadow p-7 max-w-md w-full text-center">
          <p className="text-rose-700 font-semibold mb-3">{t("verify_missing_token")}</p>
          <Link to="/login" className="btn btn-primary inline-flex">{t("back_to_login")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-pattern px-4 py-6" data-testid="reset-password-page">
      {/* Prominent language toggle — top corner */}
      <button
        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        className="fixed top-4 end-4 z-50 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-teal-200 shadow-lg rounded-full px-3 py-1.5 text-xs font-bold text-teal-800 hover:bg-teal-50 hover:border-teal-400 transition"
        data-testid="lang-toggle-reset-top"
        title={t("language")}
        aria-label={t("language")}
      >
        <Globe size={13} /> {lang === "ar" ? "English" : "عربي"}
      </button>
      <div className="w-full max-w-md">
        <Brand variant="hero" className="mb-5 sm:mb-6" />

        <div className="bg-white rounded-2xl card-shadow p-5 sm:p-7">
          {done ? (
            <div className="text-center" data-testid="reset-password-done">
              <div className="bg-emerald-50 text-emerald-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 size={32} />
              </div>
              <h2 className="text-xl font-bold text-emerald-800 mb-2">{t("password_changed")}</h2>
              <p className="text-gray-600 text-sm mb-3">{t("redirecting")}…</p>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-teal-900 mb-1 text-center">{t("reset_password_title")}</h2>
              <p className="text-sm text-gray-500 mb-5 text-center">{t("reset_password_subtitle")}</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="inp-label flex items-center gap-2"><Lock size={13} /> {t("new_password")}</label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      className="inp pe-10"
                      style={{ direction: "ltr" }}
                      data-testid="reset-new-password-input"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute top-1/2 -translate-y-1/2 end-2 text-gray-400 p-1" tabIndex={-1}>
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1 mt-1.5">
                    <Check ok={checks.length} label={t("pw_check_length")} />
                    <Check ok={checks.letter} label={t("pw_check_letter")} />
                    <Check ok={checks.digit} label={t("pw_check_digit")} />
                    <Check ok={checks.symbol} label={t("pw_check_symbol")} />
                  </div>
                </div>
                <div>
                  <label className="inp-label flex items-center gap-2"><Lock size={13} /> {t("password_confirm")}</label>
                  <input
                    type={showPw ? "text" : "password"}
                    value={form.new_password_confirm}
                    onChange={(e) => setForm({ ...form, new_password_confirm: e.target.value })}
                    className="inp"
                    style={{ direction: "ltr" }}
                    data-testid="reset-confirm-input"
                  />
                  {form.new_password_confirm && (
                    <p className={`text-[11px] mt-1 ${pwMatches ? "text-emerald-700" : "text-rose-600"}`}>
                      {pwMatches ? t("pw_match_ok") : t("pw_match_fail")}
                    </p>
                  )}
                </div>
                {error && (
                  <div className="status-expired pill px-3 py-2 w-full justify-center" data-testid="reset-error">{error}</div>
                )}
                <button
                  type="submit"
                  disabled={loading || !pwOk || !pwMatches}
                  className="btn btn-primary w-full justify-center py-3"
                  data-testid="reset-submit-button"
                >
                  {loading ? t("processing") : t("change_password")}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="flex justify-center mt-5">
          <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="btn btn-secondary" data-testid="lang-toggle-reset" title={t("language")}>
            <Globe size={14} /> {lang === "ar" ? "English" : "عربي"}
          </button>
        </div>
      </div>
    </div>
  );
}
